import { Copy, Download, Upload } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { parseShape, type ShapeGeometry, type ShapeKind, shapeToText } from '@/lib/geojson';
import { t } from '@/lib/i18n';
import { kmzToKml, parseKml, shapeToKml, shapeToKmz } from '@/lib/kml';
import { Button } from '../ui/button';
import { Dialog } from '../ui/dialog';
import { Textarea } from '../ui/form';

/** The parser's messages (English keys), translated here so the i18n check sees them. */
const PARSE_ERRORS: Record<string, string> = {
  'This is not valid JSON.': t('This is not valid JSON.'),
  'No area (Polygon) found in it.': t('No area (Polygon) found in it.'),
  'No route (LineString) found in it.': t('No route (LineString) found in it.'),
  'An area needs at least three distinct points.': t('An area needs at least three distinct points.'),
  'A route needs at least two distinct points.': t('A route needs at least two distinct points.'),
  'Some coordinates are outside the map.': t('Some coordinates are outside the map.'),
  'The coordinates look swapped: GeoJSON uses [longitude, latitude].': t(
    'The coordinates look swapped: GeoJSON uses [longitude, latitude].',
  ),
};

/**
 * A shape as GeoJSON text: to look at, copy or download (GeoJSON, KML or KMZ), and (unless
 * read-only) to edit, paste or import from a file (GeoJSON, KML or KMZ; KML is shown converted to
 * GeoJSON). Applying replaces the shape in the editor; it is saved with the form.
 */
export function GeoJsonDialog({
  open,
  onClose,
  kind,
  name,
  value,
  color,
  onApply,
  readOnly = false,
}: {
  open: boolean;
  onClose: () => void;
  kind: ShapeKind;
  /** Shape name, used in the exported Feature and the file name. */
  name: string;
  value: ShapeGeometry | null | undefined;
  /** Shape colour, used in the exported KML. */
  color?: string;
  onApply: (g: ShapeGeometry) => void;
  readOnly?: boolean;
}) {
  const [text, setText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const file = useRef<HTMLInputElement>(null);
  // biome-ignore lint/correctness/useExhaustiveDependencies: refill only when the dialog opens
  useEffect(() => {
    if (open) {
      setText(shapeToText(value, name));
      setError(null);
    }
  }, [open]);

  const apply = () => {
    const parsed = parseShape(text, kind);
    if (!parsed.ok) return setError(PARSE_ERRORS[parsed.error] ?? parsed.error);
    onApply(parsed.geometry);
    onClose();
    toast.success(t('Shape replaced: save to keep it.'));
  };
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(t('Copied'));
    } catch {
      toast.error(t('Could not copy'));
    }
  };
  const download = (format: 'geojson' | 'kml' | 'kmz') => {
    // What is in the text box (possibly edited); the stored shape if that does not parse.
    const parsed = parseShape(text, kind);
    const shape = parsed.ok ? parsed.geometry : value;
    const content =
      format === 'geojson'
        ? new Blob([text], { type: 'application/geo+json' })
        : format === 'kml'
          ? new Blob([shapeToKml(shape, name, color)], { type: 'application/vnd.google-earth.kml+xml' })
          : new Blob([(shapeToKmz(shape, name, color) ?? new Uint8Array()) as BlobPart], {
              type: 'application/vnd.google-earth.kmz',
            });
    const url = URL.createObjectURL(content);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${name.replace(/[^\p{L}\p{N}-]+/gu, '_') || 'shape'}.${format}`;
    a.click();
    URL.revokeObjectURL(url);
  };
  const importFile = async (f: File | undefined) => {
    if (!f) return;
    setError(null);
    const ext = f.name.toLowerCase().split('.').pop();
    if (ext !== 'kml' && ext !== 'kmz') {
      setText(await f.text());
      return;
    }
    try {
      const kml = ext === 'kmz' ? kmzToKml(new Uint8Array(await f.arrayBuffer())) : await f.text();
      const shape = kml ? parseKml(kml, kind) : null;
      if (!shape)
        return setError(
          kind === 'Polygon' ? t('No area (Polygon) found in it.') : t('No route (LineString) found in it.'),
        );
      setText(shapeToText(shape, name));
    } catch {
      setError(t('This file could not be read.'));
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={`GeoJSON / KML · ${name}`}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {readOnly ? t('Close') : t('Cancel')}
          </Button>
          {!readOnly && <Button onClick={apply}>{t('Apply')}</Button>}
        </>
      }
    >
      <div className="flex flex-wrap gap-1.5">
        {!readOnly && (
          <>
            <Button
              size="sm"
              variant="secondary"
              icon={<Upload className="size-4" />}
              onClick={() => file.current?.click()}
            >
              {t('Import file')}
            </Button>
            <input
              ref={file}
              type="file"
              accept=".geojson,.json,.kml,.kmz,application/geo+json,application/json,application/vnd.google-earth.kml+xml,application/vnd.google-earth.kmz"
              className="hidden"
              onChange={(e) => {
                void importFile(e.target.files?.[0]);
                e.target.value = '';
              }}
            />
          </>
        )}
        <Button
          size="sm"
          variant="secondary"
          icon={<Copy className="size-4" />}
          onClick={copy}
          disabled={!text}
        >
          {t('Copy')}
        </Button>
        {(['geojson', 'kml', 'kmz'] as const).map((format) => (
          <Button
            key={format}
            size="sm"
            variant="secondary"
            icon={<Download className="size-4" />}
            onClick={() => download(format)}
            disabled={!text}
            title={t('Download')}
          >
            {format === 'geojson' ? 'GeoJSON' : format.toUpperCase()}
          </Button>
        ))}
      </div>
      <Textarea
        value={text}
        readOnly={readOnly}
        invalid={!!error}
        spellCheck={false}
        onChange={(e) => {
          setText(e.target.value);
          setError(null);
        }}
        placeholder={
          kind === 'Polygon'
            ? '{ "type": "Polygon", "coordinates": [[[21.16, 42.66], …]] }'
            : '{ "type": "LineString", "coordinates": [[21.16, 42.66], …] }'
        }
        className="h-72 font-mono text-xs"
      />
      {error ? (
        <p className="text-sm text-red-600">{error}</p>
      ) : (
        !readOnly && (
          <p className="text-xs text-slate-500">
            {t(
              'Paste a geometry, a Feature or a FeatureCollection, or import a GeoJSON, KML or KMZ file (the first matching shape is used). Coordinates are [longitude, latitude].',
            )}
          </p>
        )
      )}
    </Dialog>
  );
}
